import { ChangeDetectionStrategy, Component, Injector } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { definePreset } from "@primeuix/themes";
import Material from "@primeuix/themes/material-compat";
import { interval, Subscription } from "rxjs";
import { COLORS_DARK } from "themes/color-schemes/colors-dark";
import { COLORS_LIGHT } from "themes/color-schemes/colors-light";
import { PrimengService } from "./core/services/primeng.service";
import { SettingsService } from "./core/services/settings.service";
import { ThemeService } from "./core/services/theme.service";

import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { setRootInjector } from "./app.injector";
import { ToastComponent } from "./shared/components/toast/toast.component";

export const OpenBetTheme = definePreset(Material, {
    primitive: {
        ...COLORS_DARK,
        ...COLORS_LIGHT,
        // ...OB_COLOR_SCALES
    },
    semantic: {
        colorScheme: {
            light: {
                primary: {
                    color: "{l.primary.main}",
                    inverseColor: "{l.primary.contrast}",
                    hoverColor: "{l.primary.hover}",
                    activeColor: "{l.primary.selected}",
                    deep: "{l.primary.deep}",
                    lite1: "{l.primary.light1}",
                    lite2: "{l.primary.light2}",
                    lite3: "{l.primary.light3}",
                    0: "#ffffff",
                    50: "#E7E9F1",
                    100: "#B3BAD3",
                    200: "#9099BE",
                    300: "#5AD6AA1",
                    400: "#3D4D8E",
                    500: "#0D2172",
                    600: "#0C1E68",
                    700: "#091751",
                    800: "#07123F",
                    900: "#050E30",
                    950: "#050E30",
                },
                secondary: {
                    main: "{l.secondary.main}",
                    deep: "{l.secondary.deep}",
                    contrast: "{l.secondary.contrast}",
                },
                surface: {
                    0: "{l.background.components}",
                    200: "{l.background.fill}",
                    500: "{l.background.components}",
                    600: "{l.background.menu}",
                    700: "{l.primary.hover}",
                    900: "{l.background.components}",
                },
                neutral: {
                    0: "{l.neutral.0}",
                    50: "{l.neutral.50}",
                    100: "{l.neutral.100}",
                    200: "{l.neutral.200}",
                    300: "{l.neutral.300}",
                    400: "{l.neutral.400}",
                    500: "{l.neutral.500}",
                    600: "{l.neutral.600}",
                    700: "{l.neutral.700}",
                    800: "{l.neutral.800}",
                    900: "{l.neutral.900}",
                    1000: "{l.neutral.1000}",
                },
                background: {
                    main: "{l.background.main}",
                    comp: "{l.background.components}",
                    fill: "{l.background.fill}",
                    menu: "{l.background.menu}",
                    filter_header: "{l.background.filter_header}",
                },
                button: {
                    primary: "{l.button.primary}",
                    primary_text: "{l.button.primary_text}",
                    secondary: "{l.button.secondary}",
                    secondary_text: "{l.button.secondary_text}",
                    tertiary: "{l.button.tertiary}",
                    tertiary_text: "{l.button.tertiary_text}",
                },
                divider: {
                    divider: "{l.divider.divider}",
                },
                tag: {
                    role: {
                        admin: {
                            bg: "{l.tag.role.admin.bg}",
                            text: "{l.tag.role.admin.text}",
                        },
                        operator: {
                            bg: "{l.tag.role.operator.bg}",
                            text: "{l.tag.role.operator.text}",
                        },
                        tech: {
                            bg: "{l.tag.role.tech.bg}",
                            text: "{l.tag.role.tech.text}",
                        },
                        managed_else_where: {
                            bg: "{l.tag.role.managed_else_where.bg}",
                            text: "{l.tag.role.managed_else_where.text}",
                        },
                        custom: {
                            bg: "{l.tag.role.custom.bg}",
                            text: "{l.tag.role.custom.text}",
                        },
                    },
                    area: {
                        rg: {
                            bg: "{l.tag.area.rg.bg}",
                            text: "{l.tag.area.rg.text}",
                        },
                        aml: {
                            bg: "{l.tag.area.aml.bg}",
                            text: "{l.tag.area.aml.text}",
                        },
                    },
                },
                link: {
                    link: "{l.link.link}",
                    colored: "{l.link.colored}",
                },
                alerts: {
                    success: {
                        background: "{l.alerts.success.background}",
                        color: "{l.alerts.success.color}",
                    },
                    warning: {
                        background: "{l.alerts.warning.background}",
                        color: "{l.alerts.warning.color}",
                    },
                    error: {
                        background: "{l.alerts.error.background}",
                        color: "{l.alerts.error.color}",
                    },
                    info: {
                        background: "{l.alerts.info.background}",
                        color: "{l.alerts.info.color}",
                    },
                },
                overlay: {
                    popover: {
                        background: "{l.background.menu}",
                    },
                },
                highlight: {
                    background: "{l.primary.selected}",
                    focusBackground: "{l.primary.selected}",
                    color: "{l.text.primary}",
                    focusColor: "{l.text.primary}",
                },
                formField: {
                    background: "{l.background.fill}",
                    hoverBorderColor: "{l.primary.outlined_border}",
                    disabledBackground: "{surface.200}",
                    filledBackground: "{surface.50}",
                    filledFocusBackground: "{surface.50}",
                    borderColor: "{surface.300}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{l.status.error.main}",
                    color: "{l.text.primary}",
                    disabledColor: "{l.status.action.disabled}",
                    placeholderColor: "{l.text.secondary}",
                    floatLabelColor: "{l.text.secondary}",
                    floatLabelFocusColor: "{l.primary.main}",
                    floatLabelInvalidColor: "{l.status.error.main}",
                    iconColor: "{surface.400}",
                },
                graphical: {
                    1: "{l.graphical.1}",
                    2: "{l.graphical.2}",
                    3: "{l.graphical.3}",
                    4: "{l.graphical.4}",
                    5: "{l.graphical.5}",
                    6: "{l.graphical.6}",
                    7: "{l.graphical.7}",
                    8: "{l.graphical.8}",
                    9: "{l.graphical.9}",
                },
                text: {
                    color: "{l.text.primary}",
                    hoverColor: "{l.text.secondary}",
                    mutedColor: "{l.text.disabled}",
                    secondary: "{l.text.secondary}",
                    hoverMutedColor: "{l.text.secondary}",
                    contrast: "{l.text.contrast}",
                },

                highlights: {
                    lite: "{l.highlights.lite}",
                    main: "{l.highlights.main}",
                    deep: "{l.highlights.deep}",
                    contrast_text: "{l.highlights.contrast_text}",
                    hover: "{l.highlights.hover}",
                    selected: "{l.highlights.selected}",
                    focus: "{l.highlights.focus}",
                    outline_border: "{l.highlights.outline_border}",
                },

                obhighlight: "{l.highlight}",
                decoration: {
                    decoration2: "{l.decoration.decoration2}",
                },
                chip: {
                    enabledBorderColor: "{l.neutral.300}",
                },
                status: {
                    success: {
                        lite: "{l.status.success.lite}",
                        main: "{l.status.success.main}",
                        deep: "{l.status.success.deep}",
                        contrastText: "{l.status.success.contrast_text}",
                        hover: "{l.status.success.hover}",
                        selected: "{l.status.success.selected}",
                        focus: "{l.status.success.focus}",
                        outlineBorder: "{l.status.success.outline_border}",
                    },
                    warning: {
                        lite: "{l.status.warning.lite}",
                        main: "{l.status.warning.main}",
                        deep: "{l.status.warning.deep}",
                        contrastText: "{l.status.warning.contrast_text}",
                        hover: "{l.status.warning.hover}",
                        selected: "{l.status.warning.selected}",
                        focus: "{l.status.warning.focus}",
                        outlineBorder: "{l.status.warning.outline_border}",
                    },
                    error: {
                        lite: "{l.status.error.lite}",
                        main: "{l.status.error.main}",
                        deep: "{l.status.error.deep}",
                        contrastText: "{l.status.error.contrast_text}",
                        hover: "{l.status.error.hover}",
                        selected: "{l.status.error.selected}",
                        focus: "{l.status.error.focus}",
                        outlineBorder: "{l.status.error.outline_border}",
                    },
                    info: {
                        lite: "{l.status.info.lite}",
                        main: "{l.status.info.main}",
                        deep: "{l.status.info.deep}",
                        contrastText: "{l.status.info.contrast_text}",
                        hover: "{l.status.info.hover}",
                        selected: "{l.status.info.selected}",
                        focus: "{l.status.info.focus}",
                        outlineBorder: "{l.status.info.outline_border}",
                    },
                    action: {
                        active: "{l.status.action.active}",
                        hover: "{l.status.action.hover}",
                        selected: "{l.status.action.selected}",
                        focus: "{l.status.action.focus}",
                        disabled: "{l.status.action.disabled}",
                        disabled_background:
                            "{l.status.action.disabled_background}",
                    },
                },
                priorities: {
                    1: {
                        background: "{l.priorities.1.background}",
                        text: "{l.priorities.1.text}",
                    },
                    2: {
                        background: "{l.priorities.2.background}",
                        text: "{l.priorities.2.text}",
                    },
                    3: {
                        background: "{l.priorities.3.background}",
                        text: "{l.priorities.3.text}",
                    },
                    4: {
                        background: "{l.priorities.4.background}",
                        text: "{l.priorities.4.text}",
                    },
                    5: {
                        background: "{l.priorities.5.background}",
                        text: "{l.priorities.5.text}",
                    },
                },
                case_statuses: {
                    open: {
                        background: `{l.case_statuses.open.background}`,
                        surface1: "{l.case_statuses.open.surface1}",
                    },
                    in_progress: {
                        background: `{l.case_statuses.in_progress.background}`,
                        surface1: "{l.case_statuses.in_progress.surface1}",
                    },

                    closed: {
                        background: `{l.case_statuses.closed.background}`,
                        surface1: "{l.case_statuses.closed.surface1}",
                    },
                    error: {
                        background: `{l.case_statuses.error.background}`,
                        surface1: "{l.case_statuses.error.surface1}",
                    },
                },
                risk_scores: {
                    none: {
                        background: "{l.risk_scores.none.background}",
                        color: "{l.risk_scores.none.color}",
                        text: "{l.risk_scores.none.text}",
                    },
                    low: {
                        background: "{l.risk_scores.low.background}",
                        color: "{l.risk_scores.low.color}",
                        text: "{l.risk_scores.low.text}",
                    },
                    medium: {
                        background: "{l.risk_scores.medium.background}",
                        color: "{l.risk_scores.medium.color}",
                        text: "{l.risk_scores.medium.text}",
                    },
                    high: {
                        background: "{l.risk_scores.high.background}",
                        color: "{l.risk_scores.high.color}",
                        text: "{l.risk_scores.high.text}",
                    },
                },
                timelineKpis: {
                    amountBet: {
                        base: "{l.timelineKpis.amountBet.base}",
                        gradient: "{l.timelineKpis.amountBet.gradient}",
                    },
                    amountLostWon: {
                        base: "{l.timelineKpis.amountLostWon.base}",
                        gradient: "{l.timelineKpis.amountLostWon.gradient}",
                    },
                    amountDeposited: {
                        base: "{l.timelineKpis.amountDeposited.base}",
                        gradient: "{l.timelineKpis.amountDeposited.gradient}",
                    },
                    numberOfDeposits: {
                        base: "{l.timelineKpis.numberOfDeposits.base}",
                        gradient: "{l.timelineKpis.numberOfDeposits.gradient}",
                    },
                    amountWithdrawn: {
                        base: "{l.timelineKpis.amountWithdrawn.base}",
                        gradient: "{l.timelineKpis.amountWithdrawn.gradient}",
                    },
                    playDuration: {
                        base: "{l.timelineKpis.playDuration.base}",
                        gradient: "{l.timelineKpis.playDuration.gradient}",
                    },
                    rgRiskScore: {
                        base: "{l.timelineKpis.rgRiskScore.base}",
                        gradient: "{l.timelineKpis.rgRiskScore.gradient}",
                    },
                    amlRiskScore: {
                        base: "{l.timelineKpis.amlRiskScore.base}",
                        gradient: "{l.timelineKpis.amlRiskScore.gradient}",
                    },
                },
                timelineEvents: {
                    interaction: {
                        base: "{l.timelineEvents.interaction.base}",
                        gradient: "{l.timelineEvents.interaction.gradient}",
                    },
                    playbreak: {
                        base: "{l.timelineEvents.playbreak.base}",
                        gradient: "{l.timelineEvents.playbreak.gradient}",
                    },
                    limitChange: {
                        base: "{l.timelineEvents.limitChange.base}",
                        gradient: "{l.timelineEvents.limitChange.gradient}",
                    },
                    incident: {
                        base: "{l.timelineEvents.incident.base}",
                        gradient: "{l.timelineEvents.incident.gradient}",
                    },
                    message: {
                        base: "{l.timelineEvents.message.base}",
                        gradient: "{l.timelineEvents.message.gradient}",
                    },
                    timeLimit: {
                        base: "{l.timelineEvents.timeLimit.base}",
                        gradient: "{l.timelineEvents.timeLimit.gradient}",
                    },
                    moneyLimit: {
                        base: "{l.timelineEvents.moneyLimit.base}",
                        gradient: "{l.timelineEvents.moneyLimit.gradient}",
                    },
                    limitExceeded: {
                        base: "{l.timelineEvents.limitExceeded.base}",
                        gradient: "{l.timelineEvents.limitExceeded.gradient}",
                    },
                    login: {
                        base: "{l.timelineEvents.login.base}",
                        gradient: "{l.timelineEvents.login.gradient}",
                    },
                },
                neccton: {
                    loadingIndicator: {
                        color: "{l.neccton.loading_indicator.color}",
                    },
                },
            },
            dark: {
                // app: {
                //     header: {
                //         logo: {
                //             path: './assets/logo/Neccton-Logo-dark.svg'
                //         }
                //     }
                // },
                primary: {
                    color: "{d.primary.main}",
                    inverseColor: "{d.primary.contrast}",
                    hoverColor: "{d.primary.hover}",
                    activeColor: "{d.primary.selected}",
                    deep: "{d.primary.deep}",
                    lite1: "{d.primary.light1}",
                    lite2: "{d.primary.light2}",
                    lite3: "{d.primary.light3}",
                    0: "#ffffff",
                    50: "#F2F7FD",
                    100: "#D6E5FA",
                    200: "#C2D8F8",
                    300: "#A7C7F4",
                    400: "#95BCF2",
                    500: "#7BABEF",
                    600: "#709CD9",
                    700: "#5779AA",
                    800: "#445E83",
                    900: "#344864",
                    950: "#344864",
                },
                secondary: {
                    main: "{d.secondary.main}",
                    deep: "{d.secondary.deep}",
                    contrast: "{d.secondary.contrast}",
                },
                surface: {
                    0: "{d.background.components}",
                    200: "{d.background.fill}",
                    500: "{d.background.components}",
                    600: "{d.background.menu}",
                    700: "{d.primary.hover}",
                    900: "{d.background.components}",
                },
                background: {
                    main: "{d.background.main}",
                    comp: "{d.background.components}",
                    fill: "{d.background.fill}",
                    menu: "{d.background.menu}",
                    filter_header: "{d.background.filter_header}",
                },
                button: {
                    primary: "{d.button.primary}",
                    primary_text: "{d.button.primary_text}",
                    secondary: "{d.button.secondary}",
                    secondary_text: "{d.button.secondary_text}",
                    tertiary: "{d.button.tertiary}",
                    tertiary_text: "{d.button.tertiary_text}",
                },
                divider: {
                    divider: "{d.divider.divider}",
                },
                tag: {
                    role: {
                        admin: {
                            bg: "{d.tag.role.admin.bg}",
                            text: "{d.tag.role.admin.text}",
                        },
                        operator: {
                            bg: "{d.tag.role.operator.bg}",
                            text: "{d.tag.role.operator.text}",
                        },
                        tech: {
                            bg: "{d.tag.role.tech.bg}",
                            text: "{d.tag.role.tech.text}",
                        },
                        managed_else_where: {
                            bg: "{d.tag.role.managed_else_where.bg}",
                            text: "{d.tag.role.managed_else_where.text}",
                        },
                        custom: {
                            bg: "{d.tag.role.custom.bg}",
                            text: "{d.tag.role.custom.text}",
                        },
                    },
                    area: {
                        rg: {
                            bg: "{d.tag.area.rg.bg}",
                            text: "{d.tag.area.rg.text}",
                        },
                        aml: {
                            bg: "{d.tag.area.aml.bg}",
                            text: "{d.tag.area.aml.text}",
                        },
                    },
                },
                link: {
                    link: "{d.link.link}",
                    colored: "{d.link.colored}",
                },
                alerts: {
                    success: {
                        background: "{d.alerts.success.background}",
                        color: "{d.alerts.success.color}",
                    },
                    warning: {
                        background: "{d.alerts.warning.background}",
                        color: "{d.alerts.warning.color}",
                    },
                    error: {
                        background: "{d.alerts.error.background}",
                        color: "{d.alerts.error.color}",
                    },
                    info: {
                        background: "{d.alerts.info.background}",
                        color: "{d.alerts.info.color}",
                    },
                },
                neutral: {
                    0: "{d.neutral.0}",
                    50: "{d.neutral.50}",
                    100: "{d.neutral.100}",
                    200: "{d.neutral.200}",
                    300: "{d.neutral.300}",
                    400: "{d.neutral.400}",
                    500: "{d.neutral.500}",
                    600: "{d.neutral.600}",
                    700: "{d.neutral.700}",
                    800: "{d.neutral.800}",
                    900: "{d.neutral.900}",
                    1000: "{d.neutral.1000}",
                },
                overlay: {
                    popover: {
                        background: "{d.background.menu}",
                    },
                },

                highlights: {
                    lite: "{d.highlights.lite}",
                    main: "{d.highlights.main}",
                    deep: "{d.highlights.deep}",
                    contrast_text: "{d.highlights.contrast_text}",
                    hover: "{d.highlights.hover}",
                    selected: "{d.highlights.selected}",
                    focus: "{d.highlights.focus}",
                    outline_border: "{d.highlights.outline_border}",
                },

                highlight: {
                    background: "{d.primary.selected}",
                    focusBackground: "{d.primary.selected}",
                    color: "{d.text.primary}",
                    focusColor: "{d.text.primary}",
                },
                formField: {
                    background: "{d.background.fill}",
                    hoverBorderColor: "{d.primary.outlined_border}",
                    disabledBackground: "{surface.200}",
                    filledBackground: "{surface.50}",
                    filledFocusBackground: "{surface.50}",
                    borderColor: "{d.neutral.300}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{d.status.error.main}",
                    color: "{d.text.primary}",
                    disabledColor: "{d.status.action.disabled}",
                    // disabledColor: '{surface.500}',
                    placeholderColor: "{d.text.secondary}",
                    floatLabelColor: "{d.text.secondary}",
                    floatLabelFocusColor: "{d.primary.main}",
                    floatLabelInvalidColor: "{d.status.error.main}",
                    iconColor: "{surface.400}",
                },
                decoration: {
                    decoration2: "{d.decoration.decoration2}",
                },
                chip: {
                    enabledBorderColor: "{d.neutral.300}",
                },
                graphical: {
                    1: "{d.graphical.1}",
                    2: "{d.graphical.2}",
                    3: "{d.graphical.3}",
                    4: "{d.graphical.4}",
                    5: "{d.graphical.5}",
                    6: "{d.graphical.6}",
                    7: "{d.graphical.7}",
                    8: "{d.graphical.8}",
                    9: "{d.graphical.9}",
                },
                text: {
                    color: "{d.text.primary}",
                    hoverColor: "{d.text.secondary}",
                    secondary: "{d.text.secondary}",
                    mutedColor: "{d.text.disabled}",
                    hoverMutedColor: "{d.text.secondary}",
                    contrast: "{d.text.contrast}",
                },
                obhighlight: "{d.highlight}",
                status: {
                    success: {
                        lite: "{d.status.success.lite}",
                        main: "{d.status.success.main}",
                        deep: "{d.status.success.deep}",
                        contrastText: "{d.status.success.contrast_text}",
                        hover: "{d.status.success.hover}",
                        selected: "{d.status.success.selected}",
                        focus: "{d.status.success.focus}",
                        outlineBorder: "{d.status.success.outline_border}",
                    },
                    warning: {
                        lite: "{d.status.warning.lite}",
                        main: "{d.status.warning.main}",
                        deep: "{d.status.warning.deep}",
                        contrastText: "{d.status.warning.contrast_text}",
                        hover: "{d.status.warning.hover}",
                        selected: "{d.status.warning.selected}",
                        focus: "{d.status.warning.focus}",
                        outlineBorder: "{d.status.warning.outline_border}",
                    },
                    error: {
                        lite: "{d.status.error.lite}",
                        main: "{d.status.error.main}",
                        deep: "{d.status.error.deep}",
                        contrastText: "{d.status.error.contrast_text}",
                        hover: "{d.status.error.hover}",
                        selected: "{d.status.error.selected}",
                        focus: "{d.status.error.focus}",
                        outlineBorder: "{d.status.error.outline_border}",
                    },
                    info: {
                        lite: "{d.status.info.lite}",
                        main: "{d.status.info.main}",
                        deep: "{d.status.info.deep}",
                        contrastText: "{d.status.info.contrast_text}",
                        hover: "{d.status.info.hover}",
                        selected: "{d.status.info.selected}",
                        focus: "{d.status.info.focus}",
                        outlineBorder: "{d.status.info.outline_border}",
                    },
                    action: {
                        active: "{d.status.action.active}",
                        hover: "{d.status.action.hover}",
                        selected: "{d.status.action.selected}",
                        focus: "{d.status.action.focus}",
                        disabled: "{d.status.action.disabled}",
                        disabled_background:
                            "{d.status.action.disabled_background}",
                    },
                },
                priorities: {
                    1: {
                        background: "{d.priorities.1.background}",
                        text: "{d.priorities.1.text}",
                    },
                    2: {
                        background: "{d.priorities.2.background}",
                        text: "{d.priorities.2.text}",
                    },
                    3: {
                        background: "{d.priorities.3.background}",
                        text: "{d.priorities.3.text}",
                    },
                    4: {
                        background: "{d.priorities.4.background}",
                        text: "{d.priorities.4.text}",
                    },
                    5: {
                        background: "{d.priorities.5.background}",
                        text: "{d.priorities.5.text}",
                    },
                },
                case_statuses: {
                    open: {
                        background: `{d.case_statuses.open.background}`,
                        surface1: "{d.case_statuses.open.surface1}",
                    },
                    in_progress: {
                        background: `{d.case_statuses.in_progress.background}`,
                        surface1: "{d.case_statuses.in_progress.surface1}",
                    },

                    closed: {
                        background: `{d.case_statuses.closed.background}`,
                        surface1: "{d.case_statuses.closed.surface1}",
                    },
                    error: {
                        background: `{d.case_statuses.error.background}`,
                        surface1: "{d.case_statuses.error.surface1}",
                    },
                },
                risk_scores: {
                    none: {
                        background: "{d.risk_scores.none.background}",
                        color: "{d.risk_scores.none.color}",
                        text: "{d.risk_scores.none.text}",
                    },
                    low: {
                        background: "{d.risk_scores.low.background}",
                        color: "{d.risk_scores.low.color}",
                        text: "{d.risk_scores.low.text}",
                    },
                    medium: {
                        background: "{d.risk_scores.medium.background}",
                        color: "{d.risk_scores.medium.color}",
                        text: "{d.risk_scores.medium.text}",
                    },
                    high: {
                        background: "{d.risk_scores.high.background}",
                        color: "{d.risk_scores.high.color}",
                        text: "{d.risk_scores.high.text}",
                    },
                },
                timelineKpis: {
                    amountBet: {
                        base: "{d.timelineKpis.amountBet.base}",
                        gradient: "{d.timelineKpis.amountBet.gradient}",
                    },
                    amountLostWon: {
                        base: "{d.timelineKpis.amountLostWon.base}",
                        gradient: "{d.timelineKpis.amountLostWon.gradient}",
                    },
                    amountDeposited: {
                        base: "{d.timelineKpis.amountDeposited.base}",
                        gradient: "{d.timelineKpis.amountDeposited.gradient}",
                    },
                    numberOfDeposits: {
                        base: "{d.timelineKpis.numberOfDeposits.base}",
                        gradient: "{d.timelineKpis.numberOfDeposits.gradient}",
                    },
                    amountWithdrawn: {
                        base: "{d.timelineKpis.amountWithdrawn.base}",
                        gradient: "{d.timelineKpis.amountWithdrawn.gradient}",
                    },
                    playDuration: {
                        base: "{d.timelineKpis.playDuration.base}",
                        gradient: "{d.timelineKpis.playDuration.gradient}",
                    },
                    rgRiskScore: {
                        base: "{d.timelineKpis.rgRiskScore.base}",
                        gradient: "{d.timelineKpis.rgRiskScore.gradient}",
                    },
                    amlRiskScore: {
                        base: "{d.timelineKpis.amlRiskScore.base}",
                        gradient: "{d.timelineKpis.amlRiskScore.gradient}",
                    },
                },
                timelineEvents: {
                    interaction: {
                        base: "{d.timelineEvents.interaction.base}",
                        gradient: "{d.timelineEvents.interaction.gradient}",
                    },
                    playbreak: {
                        base: "{d.timelineEvents.playbreak.base}",
                        gradient: "{d.timelineEvents.playbreak.gradient}",
                    },
                    limitChange: {
                        base: "{d.timelineEvents.limitChange.base}",
                        gradient: "{d.timelineEvents.limitChange.gradient}",
                    },
                    incident: {
                        base: "{d.timelineEvents.incident.base}",
                        gradient: "{d.timelineEvents.incident.gradient}",
                    },
                    message: {
                        base: "{d.timelineEvents.message.base}",
                        gradient: "{d.timelineEvents.message.gradient}",
                    },
                    timeLimit: {
                        base: "{d.timelineEvents.timeLimit.base}",
                        gradient: "{d.timelineEvents.timeLimit.gradient}",
                    },
                    moneyLimit: {
                        base: "{d.timelineEvents.moneyLimit.base}",
                        gradient: "{d.timelineEvents.moneyLimit.gradient}",
                    },
                    limitExceeded: {
                        base: "{d.timelineEvents.limitExceeded.base}",
                        gradient: "{d.timelineEvents.limitExceeded.gradient}",
                    },
                    login: {
                        base: "{d.timelineEvents.login.base}",
                        gradient: "{d.timelineEvents.login.gradient}",
                    },
                },
                neccton: {
                    loadingIndicator: {
                        color: "{d.neccton.loading_indicator.color}",
                    },
                },
            },
        },
    },
    components: {
        popover: {
            content: {
                padding: "8px",
            },
        },
        skeleton: {
            animation: {
                background:
                    "linear-gradient(90deg, {surface.200} 0%, {surface.500} 50%, {surface.200} 100%)",
            },
            background: "black",
        },
    },
} as any);

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, ConfirmDialogModule, ToastComponent],
})
export class AppComponent {
    private subscriptions: Subscription[] = [];

    private timer = interval(10);

    constructor(
        private translate: TranslateService,
        private themeService: ThemeService,
        private settingsService: SettingsService,
        private primeService: PrimengService,
        injector: Injector,
    ) {
        setRootInjector(injector);
        this.primeService.initPrime();
        this.translate.setFallbackLang("en_US");
        // this.adminService.listUsers();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.settingsService.appSettings$.subscribe((appSettings) => {
                this.translate.use(appSettings.languageLocale);
                this.themeService.toggleTheme(
                    appSettings.useDarkMode,
                    appSettings.theme,
                );
                //this.fontService.setFontSize(appSettings.fontSize);
                // this.api.testEventSource()
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
